import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, getBaseUrl } from '../../utils/seo';

const HomePage = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // SEO設定
  useSEO({
    title: '岡山市の解体工事・造園工事・不用品処理｜おかやま解体工事株式会社',
    description: '岡山市で建物解体工事、内装解体工事、造園工事、外構エクステリア工事、不用品処理を行うおかやま解体工事株式会社。創業30年以上の実績と信頼で、岡山市全域に対応。無料見積もり・即日対応可能。お気軽にご相談ください。',
    keywords: '岡山市,解体工事,建物解体,内装解体,造園工事,外構工事,不用品処理,ゴミ屋敷片付け,原状回復,岡山',
    canonicalPath: '/',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'おかやま解体工事株式会社',
      image: `${getBaseUrl()}/logo.png`,
      description: '岡山市で建物解体工事、内装解体工事、造園工事、外構エクステリア工事、不用品処理を行う総合建設会社',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 34.6617,
        longitude: 133.9350
      },
      telephone: '+81-86-012-3456',
      faxNumber: '+81-86-012-3457',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:00',
        closes: '17:00'
      },
      priceRange: '$$',
      foundingDate: '1993'
    }
  });

  const news = [
    { date: '2025.01.15', title: '年末年始の営業時間のお知らせ' },
    { date: '2025.01.10', title: '岡山市北区での大型解体工事を完了しました' },
    { date: '2024.12.20', title: '新しい重機を導入しました' },
    { date: '2024.12.05', title: 'ホームページをリニューアルしました' },
  ];

  const services = [
    {
      title: '造園工事',
      description: '庭のリニューアルから庭の片付けまで対応',
      image: 'https://public.readdy.ai/ai/img_res/ae26809465691a93d437c37a97909821.jpg',
      icon: 'ri-plant-line',
    },
    {
      title: 'ゴミ・不用品処理',
      description: 'ゴミ屋敷の片付けや生前整理も対応可能',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20workers%20efficiently%20sorting%20and%20removing%20household%20waste%20and%20unwanted%20items%2C%20professional%20junk%20removal%20service%20in%20action%2C%20organized%20cleanup%20process%20with%20simple%20clean%20background%2C%20respectful%20and%20thorough%20work%20approach&width=600&height=700&seq=service5&orientation=portrait',
      icon: 'ri-delete-bin-line',
    },
    {
      title: '外構エクステリア工事',
      description: '駐車場・フェンス・門扉などの外構工事',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20construction%20team%20installing%20exterior%20fencing%20and%20gate%20work%2C%20professional%20exterior%20construction%20scene%20with%20modern%20materials%2C%20workers%20in%20safety%20vests%2C%20clean%20simple%20background%20focusing%20on%20the%20exterior%20work%2C%20bright%20outdoor%20setting&width=600&height=700&seq=service4&orientation=portrait',
      icon: 'ri-building-4-line',
    },
  ];

  const works = [
    {
      title: '岡山市北区 木造住宅解体工事',
      location: '岡山市北区',
      duration: '5日間',
      description: '築50年の木造住宅を安全かつ迅速に解体。近隣への配慮を徹底し、騒音・粉塵対策を万全に実施しました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=old%20traditional%20Japanese%20wooden%20house%20before%20demolition%20in%20Okayama%20city%20residential%20area%2C%20aged%20structure%20showing%20wear%2C%20simple%20clean%20background%2C%20daytime%20photography%2C%20realistic%20architectural%20documentation%20style&width=500&height=600&seq=work1before&orientation=portrait',
      afterImage: 'https://readdy.ai/api/search-image?query=cleared%20empty%20land%20plot%20after%20house%20demolition%20in%20Okayama%20city%2C%20clean%20leveled%20ground%20ready%20for%20new%20construction%2C%20simple%20background%2C%20bright%20daylight%2C%20professional%20site%20preparation%20result&width=500&height=600&seq=work1after&orientation=portrait',
    },
    {
      title: '岡山市中区 店舗内装解体',
      location: '岡山市中区',
      duration: '3日間',
      description: '飲食店の原状回復工事。スケルトン状態まで解体し、オーナー様にご満足いただきました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=interior%20of%20Japanese%20restaurant%20before%20demolition%20with%20fixtures%20and%20equipment%20still%20in%20place%2C%20commercial%20space%20in%20Okayama%2C%20simple%20clean%20background%2C%20professional%20documentation%20photography&width=500&height=600&seq=work2before&orientation=portrait',
      afterImage: 'https://readdy.ai/api/search-image?query=empty%20commercial%20space%20after%20interior%20demolition%20showing%20bare%20walls%20and%20floor%2C%20skeleton%20condition%20ready%20for%20new%20tenant%2C%20clean%20simple%20background%2C%20professional%20restoration%20result%20in%20Okayama&width=500&height=600&seq=work2after&orientation=portrait',
    },
    {
      title: '岡山市南区 庭園リニューアル',
      location: '岡山市南区',
      duration: '7日間',
      description: '荒れた庭を美しい和風庭園に生まれ変わらせました。石組みと植栽のバランスにこだわりました。',
      beforeImage: 'https://readdy.ai/api/search-image?query=overgrown%20neglected%20Japanese%20garden%20before%20renovation%20with%20weeds%20and%20untended%20plants%2C%20residential%20property%20in%20Okayama%2C%20simple%20clean%20background%2C%20natural%20outdoor%20setting&width=500&height=600&seq=work3before&orientation=portrait',
      afterImage: 'https://readdy.ai/api/search-image?query=beautifully%20renovated%20Japanese%20garden%20with%20carefully%20arranged%20stones%20and%20fresh%20plantings%2C%20peaceful%20zen%20atmosphere%2C%20clean%20simple%20background%2C%20professional%20landscaping%20result%20in%20Okayama%20residential%20area&width=500&height=600&seq=work3after&orientation=portrait',
    },
  ];

  const faqs = [
    {
      question: '見積もりは無料ですか？',
      answer: 'はい、お見積もりは完全無料です。現地調査も無料で行っておりますので、お気軽にご相談ください。',
    },
    {
      question: '工事期間はどのくらいかかりますか？',
      answer: '建物の規模や構造によって異なりますが、一般的な木造住宅で3〜7日程度です。詳しくは現地調査の際にお伝えいたします。',
    },
    {
      question: '近隣への配慮はどうされていますか？',
      answer: '工事前に近隣へのご挨拶を行い、騒音・粉塵対策を徹底しています。養生シートの設置や散水による粉塵対策も万全です。',
    },
    {
      question: '解体後の廃材処理はどうなりますか？',
      answer: '適切に分別し、法令に基づいて処理いたします。マニフェスト（産業廃棄物管理票）も発行いたします。',
    },
    {
      question: '建物の一部だけの解体も可能ですか？',
      answer: 'はい、可能です。内装のみ、外壁のみなど、部分的な解体工事にも対応しております。',
    },
    {
      question: '急ぎの工事にも対応できますか？',
      answer: 'はい、スケジュールによっては即日対応も可能です。まずはお電話でご相談ください。',
    },
  ];

  const voices = [
    {
      name: '田中 一郎様',
      location: '岡山市北区',
      comment: '実家の解体をお願いしました。思い出の詰まった家でしたが、丁寧に作業していただき、近隣への配慮も素晴らしかったです。安心してお任せできました。',
      avatar: 'https://readdy.ai/api/search-image?query=portrait%20of%20middle-aged%20Japanese%20man%20in%20his%2050s%20with%20friendly%20smile%2C%20professional%20headshot%20style%2C%20simple%20clean%20neutral%20background%2C%20natural%20lighting%2C%20realistic%20photography&width=200&height=200&seq=voice1&orientation=squarish',
    },
    {
      name: '山本 美咲様',
      location: '岡山市中区',
      comment: '店舗の原状回復工事を依頼しました。短期間で綺麗に仕上げていただき、次の入居者様もすぐに決まりました。本当にありがとうございました。',
      avatar: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/2d2113dca9e909bbb26f469e313c3340.jpeg',
    },
    {
      name: '佐藤 健太様',
      location: '岡山市東区',
      comment: '庭の手入れが大変になり、リニューアルをお願いしました。提案から施工まで親身に対応していただき、理想以上の庭になりました。',
      avatar: 'https://readdy.ai/api/search-image?query=portrait%20of%20young%20Japanese%20man%20in%20his%2030s%20with%20confident%20smile%2C%20professional%20headshot%20style%2C%20simple%20clean%20neutral%20background%2C%20natural%20lighting%2C%20realistic%20photography&width=200&height=200&seq=voice3&orientation=squarish',
    },
    {
      name: '鈴木 由美様',
      location: '岡山市南区',
      comment: '不用品の処理に困っていましたが、迅速に対応していただきました。スタッフの方々も親切で、料金も明確で安心できました。',
      avatar: 'https://readdy.ai/api/search-image?query=portrait%20of%20Japanese%20woman%20in%20her%2060s%20with%20gentle%20smile%2C%20professional%20headshot%20style%2C%20simple%20clean%20neutral%20background%2C%20natural%20lighting%2C%20realistic%20photography&width=200&height=200&seq=voice4&orientation=squarish',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/fa075e4140431d61bc5d58437f224cb8.jpeg"
            alt="岡山の解体工事現場"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/20"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <div className="mb-8 inline-flex items-center space-x-4 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
              <span className="text-white text-sm font-medium">岡山市認定業者</span>
              <span className="text-white/60">|</span>
              <span className="text-white text-sm font-medium">即日対応可能</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              岡山の未来を創る、<br />確かな解体技術
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-12 font-light">
              建物解体から造園まで、地域に根ざした総合サービス
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center space-x-3 bg-white text-primary px-8 py-4 rounded-full hover:bg-secondary hover:text-white transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span>お電話でのご相談</span>
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center space-x-3 bg-white text-primary px-8 py-4 rounded-full hover:bg-secondary hover:text-white transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span>お問い合わせフォーム</span>
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
                NEWS
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">最新情報</h2>
              <p className="text-gray-600 text-lg">おかやま解体工事からのお知らせ</p>
            </div>
            <div className="lg:col-span-7 space-y-6">
              {news.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span className="text-gray-500 text-sm">{item.date}</span>
                    <h3 className="text-primary font-bold text-lg flex-1">{item.title}</h3>
                    <i className="ri-arrow-right-line text-primary hidden sm:block"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              事業内容
            </h2>
            <p className="text-white/90 text-base lg:text-lg">
              お客様のニーズに合わせた幅広いサービスを提供しています
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {services.slice(0, 3).map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg mb-4">
                    <i className={`${service.icon} text-2xl text-black`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">
                    {service.title}
                  </h3>
                  <p className="text-medium text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center space-x-2 px-8 py-3 bg-white text-black rounded-full hover:bg-gray-100 transition-all font-medium whitespace-nowrap cursor-pointer"
            >
              <span>その他サービスを見る</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Works Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 gap-6">
            <div>
              <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
                WORKS
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-2">施工事例</h2>
              <p className="text-gray-600 text-lg">実際の施工実績をご覧ください</p>
            </div>
            <Link
              to="/works"
              className="px-8 py-3 bg-primary text-white rounded-full hover:bg-secondary transition-all whitespace-nowrap cursor-pointer"
            >
              すべての事例を見る
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {works.map((work, index) => (
              <article key={index} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer flex flex-col">
                <div className="grid grid-cols-2 h-72 flex-shrink-0">
                  <div className="relative">
                    <img
                      src={work.beforeImage}
                      alt={`${work.title} 施工前`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-3 py-1 rounded text-xs">
                      施工前
                    </div>
                  </div>
                  <div className="relative">
                    <img
                      src={work.afterImage}
                      alt={`${work.title} 施工後`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-2 right-2 bg-secondary text-white px-3 py-1 rounded text-xs">
                      施工後
                    </div>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-primary font-bold text-lg sm:text-xl mb-3 break-words">{work.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <i className="ri-map-pin-line mr-2 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                    <span>{work.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm mb-4">
                    <i className="ri-time-line mr-2 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                    <span>{work.duration}</span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4 flex-1">{work.description}</p>
                  <Link to="/works" className="text-primary underline hover:text-secondary transition-colors cursor-pointer">
                    詳細を見る
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
              FAQ
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-primary">よくあるご質問</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                >
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">Q</span>
                    </div>
                    <span className="text-primary font-bold text-lg">{faq.question}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-2xl text-primary transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 pl-18">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voice Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <div className="inline-block border border-gray-300 px-4 py-1 rounded-full text-xs text-gray-600 mb-4">
              VOICE
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-primary">お客様の声</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {voices.map((voice, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-7 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={voice.avatar}
                      alt={voice.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-primary font-bold text-lg">{voice.name}</h3>
                    <p className="text-gray-500 text-sm">{voice.location}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">
                  &ldquo;{voice.comment}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20beautiful%20Okayama%20cityscape%20with%20construction%20site%20in%20foreground%2C%20professional%20Japanese%20workers%20visible%20in%20distance%2C%20bright%20sunny%20day%20with%20clear%20sky%2C%20clean%20organized%20work%20environment%2C%20inspiring%20and%20professional%20atmosphere&width=1920&height=1080&seq=services-cta&orientation=landscape"
            alt="岡山の風景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              まずはお気軽に<br />ご相談ください
            </h2>
          </div>
          <div className="absolute bottom-12 right-8 text-right">
            <p className="text-white text-xl lg:text-2xl font-light mb-4">
              無料見積もり・現地調査実施中
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center space-x-3 bg-white text-primary px-8 py-4 rounded-full hover:bg-secondary hover:text-white transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span>086-012-3456</span>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center space-x-3 bg-white text-primary px-8 py-4 rounded-full hover:bg-secondary hover:text-white transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
                <span>お問い合わせ</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
